import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ParameterTranslationItem {
  code: string;
  label: string;
}

interface ParameterTranslationFile {
  items?: ParameterTranslationItem[];
}

type ParameterTranslations = Record<string, string>;

interface UseParameterTranslationResult {
  translateParameter: (code?: string | null, fallback?: string) => string;
  isLoading: boolean;
  error: Error | null;
}

type TranslationLoader = () => Promise<{ default: ParameterTranslationFile }>;

const translationLoaders: Record<string, TranslationLoader> = {
  en: () => import('../locales/EmissionParameter/en.json'),
  de: () => import('../locales/EmissionParameter/de.json'),
  fr: () => import('../locales/EmissionParameter/fr.json'),
  cn: () => import('../locales/EmissionParameter/cn.json'),
  tr: () => import('../locales/EmissionParameter/tr.json'),
};

const normalizeLanguage = (language: string | undefined): string | undefined => {
  if (!language) {
    return undefined;
  }

  const [base] = language.split('-');
  return base?.toLowerCase();
};

const toTranslationMap = (file: ParameterTranslationFile | undefined): ParameterTranslations => {
  if (!file?.items || !Array.isArray(file.items)) {
    return {};
  }

  return file.items.reduce<ParameterTranslations>((acc, item) => {
    if (item.code) {
      acc[item.code] = item.label || item.code;
    }
    return acc;
  }, {});
};

const loadLocale = async (locale: string): Promise<ParameterTranslations | null> => {
  const loader = translationLoaders[locale];
  if (!loader) {
    return null;
  }

  const module = await loader();
  return toTranslationMap(module.default);
};

export const useParameterTranslation = (): UseParameterTranslationResult => {
  const { i18n } = useTranslation();
  const [translations, setTranslations] = useState<ParameterTranslations>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let isMounted = true;

    const fetchTranslations = async () => {
      setIsLoading(true);
      setError(null);

      const normalized = normalizeLanguage(i18n.language);
      const localesToTry = [];

      if (normalized && translationLoaders[normalized]) {
        localesToTry.push(normalized);
      }

      if (!localesToTry.includes('en') && translationLoaders.en) {
        localesToTry.push('en');
      }

      if (localesToTry.length === 0) {
        localesToTry.push('en');
      }

      try {
        for (const locale of localesToTry) {
          const map = await loadLocale(locale);
          if (map && Object.keys(map).length > 0) {
            if (isMounted) {
              setTranslations(map);
              setIsLoading(false);
            }
            return;
          }
        }

        if (isMounted) {
          setTranslations({});
          setIsLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          setError(err as Error);
          setTranslations({});
          setIsLoading(false);
        }
      }
    };

    fetchTranslations();

    return () => {
      isMounted = false;
    };
  }, [i18n.language]);

  const translateParameter = useCallback(
    (code?: string | null, fallback?: string): string => {
      if (code) {
        const trimmedCode = code.trim();
        if (trimmedCode && translations[trimmedCode]) {
          return translations[trimmedCode];
        }
      }

      if (fallback && fallback.trim().length > 0) {
        return fallback;
      }

      if (code && code.trim().length > 0) {
        return code;
      }

      return '';
    },
    [translations],
  );

  return {
    translateParameter,
    isLoading,
    error,
  };
};

